import { animated, SpringConfig, SpringValue, to, useSpring, useSpringRef } from '@react-spring/web';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { IconButton } from 'src/common/icon-button';
import { CloseStrongIcon, UndorderedListStrongIcon } from 'src/monet/icons';
import { Nullable } from 'src/types/nullable.type';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { BlockView } from '../block-view';
import { SectionThumbnail } from '../components/section-thumbnail';
import { ACTIVE_SECTION_TOP, getActiveSectionId } from '../helpers/get-active-section-id';
import { getSuperRect } from '../helpers/get-super-rect';
import { useNeueLayoutMode } from '../helpers/neue-layout-mode.hook';
import { Block, BlockRenderElement, Rect, Section } from '../types';
import { useReactSpringScrollTo } from '../utils/react-spring-scroll-to.hook';
import { usePlayerStore } from './player-store';

const DRAWER_ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

const THUMBNAIL_LAYOUT = {
  width: 50,
  height: 60,
  contentPadding: 8,
};

function getHtmlText(text: string) {
  const div = document.createElement('div');
  div.innerHTML = text;
  return div.textContent || div.innerText || '';
}

type SectionCardData = {
  section: Section;
  title: Nullable<string>;
  blockRenderElements: BlockRenderElement[];
};

export const MobilePortraitNavigationBar = () => {
  const {
    layout: { renderElements, layoutReady, sections, sectionRects, innerActualHeight },
    sectionDrawerOpen,
    innerAreaHeight,
    playerContentElement,
    canvasScrollPosition,
    setSectionDrawerOpen,
  } = usePlayerStore();
  const preventOpeningMenuRef = useRef(false);
  const { layoutMode } = useNeueLayoutMode();
  const [currentSectionId, setCurrentSectionId] = React.useState<Nullable<Section['id']>>(null);
  const [scrollToSectionId, setScrollToSectionId] = useState<Section['id'] | null>(null);

  const renderElementsMap = useMemo(() => {
    const map = new Map<Block['id'], BlockRenderElement>();
    for (const re of renderElements) {
      if (re.type === 'block') {
        map.set(re.block.id, re);
      }
    }
    return map;
  }, [renderElements]);

  const sectionCardData: SectionCardData[] = useMemo(
    () =>
      sections.map((section) => {
        let title = section.name;
        if (title === null) {
          const textBlocks = section.blocks.filter((block) => block.content.type === 'text');
          for (const block of textBlocks) {
            if (block.content.type === 'text') {
              title = getHtmlText(block.content.value);
            }
            if (title) {
              break;
            }
          }
        }
        const blockRenderElements = section.blocks
          .map((block) => renderElementsMap.get(block.id))
          .filter(Boolean) as BlockRenderElement[];

        return {
          section,
          title: title,
          blockRenderElements,
        };
      }),
    [sections, renderElementsMap]
  );

  const updateActiveSectionId = useRefCallback(() => {
    if (!innerAreaHeight) return null;
    if (!innerActualHeight) return null;
    if (sections.length === 0) return null;
    const scrollBasedActiveId = getActiveSectionId({
      sections,
      sectionRects,
      innerAreaHeight,
      innerActualHeight,
      canvasScrollPosition,
    });
    if (!scrollBasedActiveId) return;
    if (scrollToSectionId) return;
    if (scrollBasedActiveId === currentSectionId) return;
    console.log('DesktopSectionDrawer: update active section', scrollBasedActiveId);
    setCurrentSectionId(scrollBasedActiveId);
  }, [
    innerAreaHeight,
    innerActualHeight,
    sections,
    sectionRects,
    canvasScrollPosition,
    currentSectionId,
    scrollToSectionId,
  ]);

  useEffect(() => {
    updateActiveSectionId();
  }, [canvasScrollPosition, innerActualHeight, innerAreaHeight, sections.length, updateActiveSectionId]);

  const currentSectionIndex = sections.findIndex((section) => section.id === currentSectionId);

  const containerRef = useRef<HTMLDivElement>(null);
  useClickAway(containerRef, () => {
    setSectionDrawerOpen(false);
    preventOpeningMenuRef.current = false;
  });

  const drawerStyles = useSpring({
    drawerOpacity: sectionDrawerOpen ? 1 : 0,
    menuButtonOpacity: sectionDrawerOpen ? 0 : 1,
    y: 0,
    config: DRAWER_ANIMATION_CONFIG,
    // config: { duration: 2000 },
  });

  const { scrollTo } = useReactSpringScrollTo();

  useEffect(() => {
    if (scrollToSectionId && playerContentElement) {
      const sectionIndex = sections.findIndex((s) => s.id === currentSectionId);
      if (sectionIndex === -1) {
        return;
      }
      const sectionRect = sectionRects[sectionIndex];
      if (!sectionRect) {
        return;
      }
      let scrollY = sectionRect.y - ACTIVE_SECTION_TOP;
      scrollY = Math.max(scrollY, 0);
      scrollTo(playerContentElement, scrollY, () => {
        setScrollToSectionId(null);
      });
    }
  }, [scrollToSectionId]);

  return (
    <>
      <animated.div
        className={classNames(
          'absolute top-0 portrait:inset-x-4 w-auto bottom-14 flex flex-col items-center justify-end',
          'landscape:bottom-2 landscape:w-[343px] landscape:right-[161px]',
          {
            'opacity-0': !layoutReady,
          }
        )}
        style={{
          opacity: drawerStyles.drawerOpacity,
          transform: drawerStyles.y.to((y) => `translateY(${y}px)`),
        }}
      >
        <div
          ref={containerRef}
          className={classNames('flex flex-col gap-2 rounded-t-lg w-full max-h-[80vh] landscape:max-h-[75vh]', {
            'pointer-events-auto': sectionDrawerOpen,
          })}
        >
          <animated.div className='flex flex-col gap-1 p-1 overflow-y-auto text-white bg-neue-glass neue-glass-backdrop-blur rounded-lg'>
            {sectionCardData.map(({ section, title, blockRenderElements }, i) => {
              return (
                <div
                  className={classNames(
                    'flex flex-1 items-center gap-2 transition-opacity cursor-pointer hover:opacity-100',
                    {
                      'opacity-70': i !== currentSectionIndex,
                      'opacity-100': i === currentSectionIndex,
                    }
                  )}
                  onClick={() => {
                    console.log('DesktopSectionDrawer: onClick: section.id: ', section.id);
                    preventOpeningMenuRef.current = false;
                    setCurrentSectionId(section.id);
                    setScrollToSectionId(section.id);
                    setSectionDrawerOpen(false);
                  }}
                >
                  <SectionThumbnail
                    thumbnailLayout={THUMBNAIL_LAYOUT}
                    blockRenderElements={blockRenderElements}
                    layoutMode={layoutMode}
                  />
                  <div
                    className='flex flex-1 text-bedrock-mobile-input items-center line-clamp-2'
                    style={{ wordBreak: 'break-word' }}
                  >
                    {title}
                  </div>
                </div>
              );
            })}
          </animated.div>
        </div>
      </animated.div>
      <div className='absolute left-0 bottom-0 pointer-events-none w-full h-10 flex mb-2 items-end justify-center'>
        <div
          className={classNames(
            'flex px-4 py-[9px] items-center ml-0 justify-center gap-2 bg-bedrock-white/[0.9] backdrop-blur-12 rounded-[100px]',
            'landscape:ml-auto landscape:mr-2 landscape:rounded-r-none',
            {
              'pointer-events-auto': !preventOpeningMenuRef.current,
            }
          )}
          onClick={() => {
            if (!preventOpeningMenuRef.current) {
              preventOpeningMenuRef.current = true;
              setSectionDrawerOpen(true);
            }
          }}
        >
          {!sectionDrawerOpen ? (
            <svg
              className='text-bedrock-black'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M5.06265 5.06281L14 2L10.9373 10.9372L2 14L5.06265 5.06281Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          ) : (
            <svg
              className='w-4 h-4 text-bedrock-black'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M13 3L3 13'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M3 3L13 13'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          )}

          <span className='text-[14px] leading-[22px] antialiased font-medium text-neue-gray-0'>Journey Map</span>
        </div>
      </div>
    </>
  );
};
